import {
  CircularProgress,
  Dialog,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller, type Control } from 'react-hook-form';
import {
  defaultFeeSchedule,
  feeScheduleFromMeExpertRates,
} from '~/modules/connect/feeSchedule';
import type { FeeScheduleData } from '~/modules/connect/feeSchedule';
import { RetainerFeeBreakdownType, RetainerWorkType } from '~/gql/graphql';
import { useToast } from '~/shared/contexts/ToastContext';
import { type User, UserRole } from '~/shared/types/connect';
import { mapRequestRetainerFormToCreateInput } from '~/modules/matters/mapCreateRetainerRequestInput';
import { useRetainerMutations } from '~/modules/matters/useRetainerMutations';
import {
  BILLING_TYPE_HOURLY,
  BILLING_TYPE_FLAT,
  RATE_KEY_OTHER,
  WORK_TYPE_OPTIONS,
  requestRetainerSchema,
  type RequestRetainerFormData,
  type RequestRetainerValidatedFormData,
  type StandardRateKey,
} from '~/modules/matters/modals/requestRetainerSchema';
import {
  BillingLabel,
  computeBillingSummary,
  FormStack,
  FormWrapper,
  RetainerAgreementCheckbox,
  RetainerBillingFields,
  type BillingFormSlice,
  RetainerReceiptSummary,
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogPaper,
  StyledSubmitButton,
  useRateDisplayLabel,
} from '~/modules/matters/modals/shared';
import { ModalHeader } from '~/shared/components/ModalHeader';
import { PLATFORM_FEE_RATE } from '~/shared/constants';

const DEFAULT_HOURS = 10;

/** Placeholder for flat fee input when no default amount (user enters value). */
const FLAT_AMOUNT_PLACEHOLDER = 'e.g. 5,000';

function getDefaultValues(expert: User | null): RequestRetainerFormData {
  const hourlyRate = expert?.hourlyRate ?? 0;
  return {
    title: '',
    feeType: '',
    billingType: BILLING_TYPE_HOURLY,
    hours: DEFAULT_HOURS,
    flatAmount: 0,
    workType: '',
    workTypeOtherComment: '',
    additionalDetails: '',
    rateKey: 'consultationRate',
    customRate: hourlyRate,
    customServiceLabel: '',
  };
}

function getWorkTypeLabel(isProBono: boolean, isFlatRate: boolean): string {
  if (isProBono) {
    return 'What will this work cover?';
  }
  if (isFlatRate) {
    return 'What will this fee cover?';
  }
  return 'What will these hours cover?';
}

interface Props {
  open: boolean;
  onClose: () => void;
  matterId: string;
  /** Hired `MatterExpert.id` for this expert on the matter; required to submit. */
  matterExpertId: string | null;
  expert: User | null;
  /** Logged-in user from `me`; when ids match `expert`, hourly dropdown uses profile rates. */
  rateSourceUser?: User | null;
}

const RequestRetainerModal = ({
  open,
  onClose,
  matterId,
  matterExpertId,
  expert,
  rateSourceUser,
}: Props) => {
  const { showToast } = useToast();
  const { createRetainerRequest, isCreatingRetainerRequest } =
    useRetainerMutations();
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const feeSchedule: FeeScheduleData = useMemo(() => {
    if (
      rateSourceUser &&
      expert &&
      rateSourceUser.id === expert.id &&
      rateSourceUser.role === UserRole.EXPERT
    ) {
      return feeScheduleFromMeExpertRates({
        hourlyRate: rateSourceUser.hourlyRate,
        recordsReviewHourlyRate: rateSourceUser.recordsReviewHourlyRate,
        examinationHourlyRate: rateSourceUser.examinationHourlyRate,
        depositionHourlyRate: rateSourceUser.depositionHourlyRate,
        testimonyHourlyRate: rateSourceUser.testimonyHourlyRate,
      });
    }
    return defaultFeeSchedule(expert?.hourlyRate ?? 0);
  }, [expert, rateSourceUser]);

  const defaultValues = useMemo((): RequestRetainerFormData => {
    const base = getDefaultValues(expert);
    return {
      ...base,
      customRate: feeSchedule.consultationRate,
    };
  }, [expert, feeSchedule]);
  const {
    control,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RequestRetainerFormData>({
    resolver: zodResolver(requestRetainerSchema),
    defaultValues,
  });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, defaultValues, reset]);

  const feeType = watch('feeType', defaultValues.feeType);
  const billingType = watch('billingType', defaultValues.billingType);
  const hours = watch('hours', defaultValues.hours);
  const flatAmount = watch('flatAmount', defaultValues.flatAmount);
  const workType = watch('workType', defaultValues.workType);
  const rateKey = watch('rateKey', defaultValues.rateKey);
  const customRate = watch('customRate', defaultValues.customRate);
  const customServiceLabel = watch(
    'customServiceLabel',
    defaultValues.customServiceLabel,
  );
  const hourlyRate =
    rateKey === RATE_KEY_OTHER
      ? customRate ?? 0
      : feeSchedule[rateKey as StandardRateKey];
  const isFlatRate = billingType === BILLING_TYPE_FLAT;
  const isProBono = feeType === RetainerFeeBreakdownType.ProBono;
  const { subtotal } = computeBillingSummary(
    isProBono,
    isFlatRate,
    hours,
    hourlyRate,
    flatAmount,
    PLATFORM_FEE_RATE,
  );
  const rateDisplayLabel = useRateDisplayLabel(
    rateKey,
    customServiceLabel,
    hourlyRate,
  );

  const handleClose = () => {
    reset();
    setAgreedToTerms(false);
    onClose();
  };

  const onSubmit = async (formValues: RequestRetainerFormData) => {
    const data = formValues as RequestRetainerValidatedFormData;
    if (!agreedToTerms) {
      return;
    }
    if (!matterExpertId) {
      showToast(
        'Could not find your participation on this matter. Try refreshing the page.',
        'error',
      );
      return;
    }
    try {
      const isProBonoSubmit = data.feeType === RetainerFeeBreakdownType.ProBono;
      let selectedRate: number;
      if (isProBonoSubmit) {
        selectedRate = 0;
      } else if (data.rateKey === RATE_KEY_OTHER) {
        selectedRate = data.customRate ?? 0;
      } else {
        selectedRate = feeSchedule[data.rateKey as StandardRateKey];
      }
      const input = mapRequestRetainerFormToCreateInput({
        matterExpertId,
        formData: data,
        hourlyRateDollars: selectedRate,
      });
      await createRetainerRequest(matterId, input);
      showToast('Retainer request sent');
      handleClose();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'request_retainer' },
        extra: { matterId, matterExpertId },
      });
      const message =
        error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again.';
      showToast(message, 'error');
    }
  };

  const dialogTitle = 'Request Retainer';
  const dialogSubtitle = 'Define the scope and fee for this engagement.';
  const submitLabel = 'Submit Request';
  const hoursLabel = 'Hours requested';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperComponent={StyledDialogPaper}
    >
      <ModalHeader
        title={dialogTitle}
        subtitle={dialogSubtitle}
        onClose={handleClose}
      />
      <StyledDialogContent>
        <FormWrapper
          id="request-retainer-form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormStack>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Retainer title"
                  placeholder="e.g. Deposition prep, Initial case review"
                  required
                  error={Boolean(errors.title)}
                  helperText={errors.title?.message}
                  fullWidth
                />
              )}
            />
            <RetainerBillingFields
              control={control as unknown as Control<BillingFormSlice>}
              errors={errors}
              feeSchedule={feeSchedule}
              flatAmountPlaceholder={FLAT_AMOUNT_PLACEHOLDER}
              formIdPrefix="request-retainer"
              isProBono={isProBono}
              isFlatRate={isFlatRate}
              rateKey={rateKey}
              hoursLabel={hoursLabel}
            />
            <Controller
              name="workType"
              control={control}
              render={({ field }) => {
                const workTypeLabel = getWorkTypeLabel(isProBono, isFlatRate);
                return (
                  <FormControl
                    fullWidth
                    required
                    error={Boolean(errors.workType)}
                  >
                    <InputLabel id="request-retainer-work-type-label" shrink>
                      {workTypeLabel}
                    </InputLabel>
                    <Select
                      {...field}
                      labelId="request-retainer-work-type-label"
                      label={workTypeLabel}
                      displayEmpty
                      onChange={(e) =>
                        field.onChange(e.target.value as RetainerWorkType | '')
                      }
                    >
                      <MenuItem value="">
                        <em>Select...</em>
                      </MenuItem>
                      {WORK_TYPE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.workType && (
                      <FormHelperText>{errors.workType.message}</FormHelperText>
                    )}
                  </FormControl>
                );
              }}
            />
            {workType === RetainerWorkType.SomethingElse && (
              <Controller
                name="workTypeOtherComment"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Describe what these hours will cover"
                    placeholder="e.g. Site visit, additional document review"
                    required
                    multiline
                    rows={3}
                    error={Boolean(errors.workTypeOtherComment)}
                    helperText={errors.workTypeOtherComment?.message}
                    fullWidth
                  />
                )}
              />
            )}
            <BillingLabel variant="subtitle2" color="text.secondary">
              Additional Details
            </BillingLabel>
            <Controller
              name="additionalDetails"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Optional note for the attorney"
                  multiline
                  rows={3}
                  fullWidth
                />
              )}
            />
            {!isProBono ? (
              <RetainerReceiptSummary
                isFlatRate={isFlatRate}
                flatAmount={flatAmount}
                rateDisplayLabel={rateDisplayLabel}
                hours={hours}
                subtotal={subtotal}
              />
            ) : null}
            <RetainerAgreementCheckbox
              agreedToTerms={agreedToTerms}
              onAgreedToTermsChange={setAgreedToTerms}
            />
          </FormStack>
        </FormWrapper>
      </StyledDialogContent>
      <StyledDialogActions>
        <StyledSubmitButton
          type="submit"
          variant="contained"
          disabled={!agreedToTerms || isCreatingRetainerRequest}
          form="request-retainer-form"
          startIcon={
            isCreatingRetainerRequest ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {isCreatingRetainerRequest ? 'Submitting…' : submitLabel}
        </StyledSubmitButton>
      </StyledDialogActions>
    </Dialog>
  );
};

export default RequestRetainerModal;
